"use client";
// Composed from beui.dev/components/motion/action-swap: one copy-to-clipboard
// button for the whole app, so the Copy -> Copied swap, its timing and its
// failure handling are not re-implemented on every page.

import { Check, Copy } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import { ActionSwapBlurButton, type ActionSwapBlurButtonProps } from "./action-swap-blur";

export interface CopyButtonProps extends Omit<
  ActionSwapBlurButtonProps,
  "items" | "value" | "defaultValue" | "onValueChange" | "cycle"
> {
  /** Text to copy, or a function resolved at click time. */
  value: string | (() => string);
  label?: string;
  copiedLabel?: string;
  /** How long the "Copied" state stays, in ms. */
  resetMs?: number;
  onCopied?: (text: string) => void;
}

export function CopyButton({
  value,
  label = "Copy",
  copiedLabel = "Copied",
  resetMs = 1500,
  onCopied,
  variant = "secondary",
  size = "sm",
  shape = "sharp",
  onClick,
  ...rest
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(async () => {
    const text = typeof value === "function" ? value() : value;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Insecure context or a denied permission: leave the label on "Copy".
      return;
    }
    setCopied(true);
    onCopied?.(text);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), resetMs);
  }, [value, onCopied, resetMs]);

  return (
    <ActionSwapBlurButton
      variant={variant}
      size={size}
      shape={shape}
      cycle={false}
      value={copied ? "copied" : "idle"}
      items={[
        { id: "idle", label, icon: <Copy className="h-3.5 w-3.5" />, ariaLabel: label },
        {
          id: "copied",
          label: copiedLabel,
          // Green reads on the neutral surfaces; on a filled button keep the ink colour.
          icon: (
            <Check
              className={cn(
                "h-3.5 w-3.5",
                variant === "primary" || variant === "danger" ? "text-current" : "text-hb-success",
              )}
            />
          ),
          ariaLabel: copiedLabel,
        },
      ]}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) void copy();
      }}
      {...rest}
    />
  );
}
